import type React from 'react';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { Input, Typography, Spin, Row, Col, Form, notification } from 'antd';
import {
  DndContext,
  type DragEndEvent,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CaretDownOutlined, HolderOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import FormInput from 'components/FormInput';
import ButtonLink from 'components/theme_components/ButtonLink';
import TitlePage from 'components/theme_components/Title';
import DF from 'DisplayFramework';
import GetLabel from 'DisplayFramework/components/GetLabel';
import Title from 'components/Title';
import {
  addParRateFormula,
  getParRateFormulaByFormulaCode,
  getParRateListbyCriteria,
  type Parrateformula,
  updateParRateFormula,
} from 'services/parametersService';
import { useNavigate, useParams } from 'react-router-dom';
import Notifications from 'components/theme_components/Notifications';

const { Text } = Typography;

type RateType = 'BASE' | 'MARGE' | 'BONUS' | any;
const ParrateformulaDetails = () => {
  const SortableItem = ({
    id,
    item,
    onRemove,
    onTransfer,
    index,
  }: {
    id: string;
    item: RateType;
    onRemove?: (id: string) => void;
    onTransfer?: (id: string, direction: 'left' | 'right') => void;
    index: number;
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      cursor: 'grab',
      position: 'relative' as const,
      zIndex: isDragging ? 1000 : 1,
    };

    const rowClass =
      index !== undefined
        ? index % 2 === 0
          ? 'item-row-even'
          : 'item-row-odd'
        : 'item-row-even';
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`${rowClass} hover:bg-gray-100`}
        {...attributes}
        {...listeners}
      >
        <div className="d-flex align-center p-2">
          <div className="w-2 h-2 rounded-full bg-color-gray mr-2" />
          <HolderOutlined
            className="mr-1 holdericon"
            style={{ color: '#666' }}
          />
          <Text>{item.ratelabel}</Text>
          {item.ratecategory === 'BONUS' && onRemove && (
            <div
              className="ml-auto cursor-pointer text-red-500 hover:text-red-700"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(id);
              }}
            >
              ×
            </div>
          )}
        </div>
      </div>
    );
  };

  const CustomDropdown = ({
    title,
    open,
    onToggle,
    children,
    className,
  }: {
    title: string;
    open: boolean;
    onToggle: () => void;
    children: React.ReactNode;
    className?: string;
  }) => {
    return (
      <div className={`d ${className || ''} `}>
        <div
          className="d-flex justify-between items-center RateTitle cursor-pointer"
          onClick={onToggle}
        >
          <span>{title}</span>
          <CaretDownOutlined className={open ? 'transform rotate-180' : ''} />
        </div>
        {open && (
          <div className="border border-gray-200 border-t-0 rounded-b bg-white p-2">
            {children}
          </div>
        )}
      </div>
    );
  };

  const transformratFormulaDataToComponentFormat = (
    data: any[]
  ): Parrateformula[] => {
    return data.map((item) => {
      const ratecategory: RateType =
        item.ratecategory === 'BASE'
          ? 'BASE'
          : item.ratecategory === 'MARGE'
            ? 'MARGE'
            : item.ratecategory === 'BONUS'
              ? 'BONUS'
              : '';

      return {
        id: item.ratecode,
        ratecategory,
        ratetype: item.ratetype,
        ratelabel: item.label || item.ratecode,
        percentage: item.ratevalue,
        ratecode: item.ratecode,
        ratevalue: item.ratevalue,
        ratespread: item.ratespread,
        flagcomplex: item.flagcomplex,
        formulacode: item.formulacode,
        formulastatus: item.formulastatus,
        formulatype: item.formulatype,
        ratecodespread: item.ratecodespread,
        rateriskcost: item.rateriskcost,
      };
    });
  };
  const { id } = useParams<{
    id: string;
  }>();

  const isValidId = !!id && id !== ':id';

  const {
    data: RateFormuladetails,
    isFetching,
    refetch,
  } = useQuery(
    ['getParRateFormulaBycode', id],
    () =>
      getParRateFormulaByFormulaCode({ formulacode: id! }).then(
        (res) => res.data
      ),
    {
      enabled: isValidId,
    }
  );

  const [form] = Form.useForm();
  useEffect(() => {
    if (RateFormuladetails) {
      form?.setFieldsValue({
        formulacode: RateFormuladetails.formulacode || '',
        formulalabel: RateFormuladetails.formulalabel || '',
        formuladescription: RateFormuladetails.formuladescription || '',
        formulastatus: RateFormuladetails.formulastatus,
        formulatype: RateFormuladetails.formulatype,
        ratecode: RateFormuladetails.ratecode,
        ratelabel: RateFormuladetails.ratelabel,
        ratevalue: RateFormuladetails.ratevalue,
        rateriskcost: RateFormuladetails.rateriskcost,
        ratecodespread: RateFormuladetails.ratecodespread,
        ratespread: RateFormuladetails.ratespread,
        flagcomplex: RateFormuladetails.flagcomplex,
      });
    } else {
      form.setFieldsValue({
        ['rateformulaform']: [],
      });
    }
  }, [form, RateFormuladetails]);

  useEffect(() => {
    if (RateFormuladetails) {
      const activeItemsList: Parrateformula[] | any = [];

      if (RateFormuladetails.ratecode && RateFormuladetails.ratelabel) {
        activeItemsList.push({
          id: RateFormuladetails.ratecode,
          ratecategory: 'BASE',
          ratelabel: RateFormuladetails.ratelabel,
          percentage: RateFormuladetails.ratevalue,
          ratecode: RateFormuladetails.ratecode,
          ratevalue: RateFormuladetails.ratevalue,
          ratespread: RateFormuladetails.ratespread,
          ratetype: RateFormuladetails.formulatype,
        });
      }

      if (RateFormuladetails.ratecodespread && RateFormuladetails.ratespread) {
        activeItemsList.push({
          id: RateFormuladetails.ratecodespread,
          ratecategory: 'MARGE',
          ratelabel: RateFormuladetails.ratecodespread,
          percentage: RateFormuladetails.ratespread,
          ratecode: RateFormuladetails.ratecodespread,
          ratevalue: 0,
          ratespread: RateFormuladetails.ratespread,
        });
      }

      if (RateFormuladetails.flagbonification) {
        activeItemsList.push({
          id: '0-1',
          ratecategory: 'BONUS',
          ratelabel: 'Bonification',
          value: true as any,
          percentage: 0,
          ratecode: '',
          ratevalue: 0,
          ratespread: 0,
        });
      }

      setActiveItems(activeItemsList);
    }
  }, [RateFormuladetails]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [baseRateOpen, setBaseRateOpen] = useState(true);
  const [marginRateOpen, setMarginRateOpen] = useState(false);
  const [bonusOpen, setbonusOpen] = useState(false);

  const [activeBaseRateOpen, setActiveBaseRateOpen] = useState(true);
  const [activeMarginRateOpen, setActiveMarginRateOpen] = useState(true);
  const [activeBonusOpen, setActiveBonusOpen] = useState(true);
  const [filterValues, setFilterValues] = useState<any>({});

  const [params, setParams] = useState({
    formulacode: '',
    ratecode: '',
    ratedescription: '',
    ratetype: '',
    // ratecodespread: 0,
    ratecodespread: filterValues.ratecodespread ?? undefined,
    ratecategory: '',
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const { data: ratFormulaData, isLoading } = useQuery(
    ['getParRateListbyCriteria', params],
    () => getParRateListbyCriteria(params).then((res) => res.list || [])
  );
  const importAll = (r: any) => {
    const images: any = {};
    r.keys().map((item: any, index: any) => {
      images[item.replace('./', '')] = r(item);
    });
    return images;
  };

  const images: any = importAll(
    require.context('/src/assets/', false, /\.(png|jpg|jpe?g|svg)$/)
  );
  const allItems = useMemo(() => {
    let items: any = [];
    if (ratFormulaData && ratFormulaData.length > 0) {
      items = transformratFormulaDataToComponentFormat(ratFormulaData);
    }

    const defaultBonusItem = {
      id: '0-1',
      ratecategory: 'BONUS',
      ratelabel: 'Bonification',
      value: true as any,
      percentage: 0,
      ratecode: '',
      ratevalue: 0,
      ratespread: 0,
    };

    const hasBonusItem = items.some(
      (item: any) => item.ratecategory === 'BONUS'
    );
    if (!hasBonusItem) {
      items.push(defaultBonusItem);
    }

    return items;
  }, [ratFormulaData]);

  const [activeItems, setActiveItems] = useState<Parrateformula[] | any>([]);

  const inactiveItems = useMemo(() => {
    const activeIds = activeItems.map((item: any) => item.id);
    return allItems.filter((item: any) => !activeIds.includes(item.id));
  }, [allItems, activeItems]);
  const baseRateItems = useMemo(() => {
    return inactiveItems.filter((item: any) => item.ratecategory === 'BASE');
  }, [inactiveItems]);

  const marginRateItems = useMemo(() => {
    return inactiveItems.filter((item: any) => item.ratecategory === 'MARGE');
  }, [inactiveItems]);
  const defaultBonificationData: any[] = [
    {
      id: '0-1',
      ratecategory: 'BONUS',
      ratelabel: 'Bonification',
      value: true as any,
      percentage: 0,
      ratecode: '',
      ratevalue: 0,
      ratespread: 0,
    },
  ];

  const bonusItems = useMemo(() => {
    return inactiveItems.filter((item: any) => item.ratecategory === 'BONUS');
  }, [inactiveItems]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    const activeItem = activeItems.find((item: any) => item.id === active.id);

    if (activeItem && activeItem.ratecategory === 'BONUS') {
      const isInactiveContainer =
        bonusItems.some((item: any) => item.id === over.id) ||
        baseRateItems.some((item: any) => item.id === over.id) ||
        marginRateItems.some((item: any) => item.id === over.id);

      if (isInactiveContainer || over.id.toString().includes('droppable')) {
        setActiveItems((prev: any) =>
          prev.filter((item: any) => item.id !== active.id)
        );
        return;
      }
    }

    const item = allItems.find((item: any) => item.id === active.id);
    if (item) {
      const updatedItem: any = {
        ...item,
        ratecategory: item.ratecategory,
        percentage: item.percentage || 0,
      };
      setActiveItems((prev: any) => [
        ...prev.filter((i: any) => i.ratecategory !== item.ratecategory),
        updatedItem,
      ]);
    }
  };

  const handleTransfer = useCallback(
    (id: string, direction: 'left' | 'right') => {
      if (direction === 'right') {
        const item = allItems.find((item: any) => item.id === id);
        if (item) {
          setActiveItems((prev: any) => {
            const updatedItem: any = {
              ...item,
              ratecategory: item.ratecategory,
              percentage: item.percentage || 0,
            };
            return [
              ...prev.filter((i: any) => i.ratecategory !== item.ratecategory),
              updatedItem,
            ];
          });
        }
      } else {
        setActiveItems((prev: any) =>
          prev.filter((item: any) => item.id !== id)
        );
      }
    },
    [allItems]
  );

  const activeBaseRateItem = activeItems.find(
    (item: any) => item.ratecategory === 'BASE'
  );
  const activeMarginRateItem = activeItems.find(
    (item: any) => item.ratecategory === 'MARGE'
  );
  const activeBonusItem = activeItems.find(
    (item: any) => item.ratecategory === 'BONUS'
  );
  const navigate = useNavigate();

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields().then((res) => res).catch((err) => {
        console.error('Validation error:', err);
        throw err;
      });

      const formData = {
        id: RateFormuladetails?.id || -1,
        formulacode: values.formulacode || '',
        formulalabel: values.formulalabel || '',
        formuladescription: values.formuladescription || '',
        formulastatus: RateFormuladetails?.formulastatus || '',
        formulatype: RateFormuladetails?.formulatype || '',
        ratecode: '',
        ratelabel: '',
        ratevalue: 0,
        rateriskcost: RateFormuladetails?.rateriskcost || 0,
        ratespread: 0,
        ratecodespread: '',
        flagcomplex: false,
        flagbonification: false,
      };

      if (activeBaseRateItem) {
        formData.ratecode =
          activeBaseRateItem.ratecode || activeBaseRateItem.id;
        formData.ratelabel = activeBaseRateItem.ratelabel;
        formData.ratevalue =
          activeBaseRateItem.ratevalue || activeBaseRateItem.percentage || 0;
        formData.formulatype = activeBaseRateItem.ratetype;
      }
      if (activeMarginRateItem) {
        formData.ratespread =
          activeMarginRateItem.ratespread ||
          activeMarginRateItem.percentage ||
          0;
        formData.ratecodespread =
          activeMarginRateItem.ratecode || activeMarginRateItem.id;
      }

      if (activeBonusItem) {
        formData.flagcomplex = false;
        formData.flagbonification = true;
      }
      const sanitizedActiveItems = activeItems.map((item: any) => ({
        ...item,
        flagcomplex: false,
      }));

      if (id == undefined) {
        const response = await addParRateFormula({
          parRateFormulaRequest: {
            ...formData,
            formulacode: '-1',
            formulastatus: 'ACTIVE',
          },
        });

        if (response.code === '200') {
          Notifications({
            status: 'success',
            message: response.message,
            description: response.description,
            duration: 2,
            placement: 'top',
          });
          refetch();
          navigate('/configuration-sfs?tab=4');

          /* navigate(
             `/configuration-sfs/rateformula/${response.data.formulacode}`
           );*/
        } else {
          Notifications({
            status: 'error',
            message: response.message,
            description: response.description,
            duration: 2,
            placement: 'top',
          });
          refetch();
          navigate('/configuration-sfs?tab=4');

        }
      } else {
        const response = await updateParRateFormula({
          formulacode: formData.formulacode,
          parRateFormulaRequest: formData,
        });

        if (response.code === '200') {
          Notifications({
            status: 'success',
            message: response.message,
            description: response.description,
            duration: 2,
            placement: 'top',
          });
          refetch();
          navigate('/configuration-sfs?tab=4');

        } else {
          Notifications({
            status: 'error',
            message: response.message,
            description: response.description,
            duration: 2,
            placement: 'top',
          });
        }
      }
    } catch (error) {
      console.error('Error processing form data:', error);
      notification.error({
        message: 'Error processing form data',
        description: 'Please check your form values and try again',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    form,
    activeBaseRateItem,
    activeMarginRateItem,
    activeBonusItem,
    RateFormuladetails,
    refetch,
  ]);
  return (
    <DF.Provider pagcode="productlist">
      <div className="bg-color-gray transferSFS">
        <TitlePage
          level={2}
          comcode="ParRateFormulasDetails"
          bgColor="purple"
          noTitleOverlay
        />

        <Form form={form}>
          <div className="container">
            <Row
              className="mb-4 ut-wizard-form"
              gutter={24}
              justify="center"
              align="middle"
            >
              <Col span={20}>
                <Title
                  comcode="inforateFormulas"
                  level={4}
                  className="ut-updateTP-title mb-7 mt-10"
                />
              </Col>
              <Col span={20}>
                {id !== undefined && (
                  <FormInput
                    name="formulacode"
                    labelCol={{ span: 24 }}
                    className="color-gray mb-7"
                  >
                    <Input disabled />
                  </FormInput>
                )}
                <FormInput
                  name="formulalabel"
                  labelCol={{ span: 24 }}
                  className="color-gray mb-7"
                  rules={[{ required: true }]}
                >
                  <Input />
                </FormInput>
              </Col>
              <Col span={20}>
                <FormInput
                  name="formuladescription"
                  labelCol={{ span: 24 }}
                  className="color-gray mb-7"
                >
                  <Input />
                </FormInput>
              </Col>
              <Col span={20}>
                <Row>
                  <Col span={11} className="text-center bold">
                    <GetLabel label="nonactif" />
                  </Col>
                  <Col span={2} />
                  <Col span={11} className="text-center bold">
                    <GetLabel label="actif" />
                  </Col>
                </Row>
              </Col>

              <Col span={20}>
                {/* Drag and drop area */}
                {isLoading ? (
                  <div className="d-flex justify-center items-center h-64">
                    <Spin size="large" tip="Chargement des données..." />
                  </div>
                ) : (
                  <DndContext
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    sensors={sensors}
                  >
                    <Row justify="space-between" className="ratelist">
                      {/* Inactive column */}
                      <Col
                        xs={24}
                        md={10}
                        className="listactif"
                        id="inactive-container"
                      >
                        <div id="droppable-inactive">
                          <CustomDropdown
                            title="Taux de base"
                            open={baseRateOpen}
                            onToggle={() => setBaseRateOpen(!baseRateOpen)}
                          >
                            <SortableContext
                              items={baseRateItems.map((item: any) => item.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              {baseRateItems.map((item: any, index: any) => (
                                <SortableItem
                                  key={item.id}
                                  id={item.id}
                                  item={item}
                                  index={index}
                                  onTransfer={handleTransfer}
                                />
                              ))}
                            </SortableContext>
                          </CustomDropdown>

                          <CustomDropdown
                            title="Taux de marge"
                            open={marginRateOpen}
                            onToggle={() => setMarginRateOpen(!marginRateOpen)}
                          >
                            <SortableContext
                              items={marginRateItems.map(
                                (item: any) => item.id
                              )}
                              strategy={verticalListSortingStrategy}
                            >
                              {marginRateItems.map((item: any, index: any) => (
                                <SortableItem
                                  key={item.id}
                                  id={item.id}
                                  item={item}
                                  index={index}
                                  onTransfer={handleTransfer}
                                />
                              ))}
                            </SortableContext>
                          </CustomDropdown>

                          <CustomDropdown
                            title="Bonification"
                            open={bonusOpen}
                            onToggle={() => setbonusOpen(!bonusOpen)}
                          >
                            <SortableContext
                              items={bonusItems.map((item: any) => item.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              {bonusItems.map((item: any, index: any) => (
                                <SortableItem
                                  key={item.id}
                                  id={item.id}
                                  item={item}
                                  index={index}
                                  onTransfer={handleTransfer}
                                />
                              ))}
                            </SortableContext>
                          </CustomDropdown>
                        </div>
                      </Col>
                      <Col xs={24} md={2} className="image-center-col">
                        <div className="image-container">
                          <img
                            src={images['dragdrop.png'] || '/placeholder.svg'}
                            style={{
                              width: 70,
                              height: 70,
                            }}
                          />
                        </div>
                      </Col>
                      {/* Active column */}
                      <Col
                        xs={24}
                        md={10}
                        className="mb-4 listactif"
                        id="active-container"
                      >
                        <div id="droppable-active">
                          {activeBaseRateItem && (
                            <CustomDropdown
                              title="Taux de base"
                              open={activeBaseRateOpen}
                              onToggle={() =>
                                setActiveBaseRateOpen(!activeBaseRateOpen)
                              }
                            >
                              <SortableContext
                                items={[activeBaseRateItem].map(
                                  (item: any) => item.id
                                )}
                                strategy={verticalListSortingStrategy}
                              >
                                <SortableItem
                                  key={activeBaseRateItem.id}
                                  id={activeBaseRateItem.id}
                                  item={activeBaseRateItem}
                                  onRemove={(id) => handleTransfer(id, 'left')}
                                  onTransfer={handleTransfer}
                                  index={2}
                                />
                              </SortableContext>
                            </CustomDropdown>
                          )}
                          {activeMarginRateItem && (
                            <CustomDropdown
                              title="Taux de marge"
                              open={activeMarginRateOpen}
                              onToggle={() =>
                                setActiveMarginRateOpen(!activeMarginRateOpen)
                              }
                            >
                              <SortableContext
                                items={[activeMarginRateItem].map(
                                  (item: any) => item.id
                                )}
                                strategy={verticalListSortingStrategy}
                              >
                                <SortableItem
                                  key={activeMarginRateItem.id}
                                  id={activeMarginRateItem.id}
                                  item={activeMarginRateItem}
                                  onRemove={(id) => handleTransfer(id, 'left')}
                                  onTransfer={handleTransfer}
                                  index={2}
                                />
                              </SortableContext>
                            </CustomDropdown>
                          )}
                          {activeBonusItem && (
                            <CustomDropdown
                              title="Bonification"
                              open={activeBonusOpen}
                              onToggle={() =>
                                setActiveBonusOpen(!activeBonusOpen)
                              }
                            >
                              <SortableContext
                                items={[activeBonusItem].map(
                                  (item: any) => item.id
                                )}
                                strategy={verticalListSortingStrategy}
                              >
                                <SortableItem
                                  key={activeBonusItem.id}
                                  id={activeBonusItem.id}
                                  item={activeBonusItem}
                                  onRemove={(id) => handleTransfer(id, 'left')}
                                  onTransfer={handleTransfer}
                                  index={2}
                                />
                              </SortableContext>
                            </CustomDropdown>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </DndContext>
                )}

                {activeItems.length > 0 && (
                  <Row className="mt-8 d-flex justify-end ">
                    <Col span={10} className="listactif recapFormul">
                      <div className="font-medium block">
                        <GetLabel label="rateformula" />
                      </div>
                      <Text>
                        {activeBaseRateItem?.ratelabel} (
                        {activeBaseRateItem?.percentage}%)
                        {activeMarginRateItem &&
                          ` + ${activeMarginRateItem.ratelabel} (${activeMarginRateItem.percentage}%)`}
                        {activeBonusItem && ` + ${activeBonusItem.ratelabel}`}
                      </Text>
                    </Col>
                  </Row>
                )}
              </Col>
              <Col span={20}>
                <Row justify="end" className="mt-7">
                  <ButtonLink comcode="Annuler" className="ut-btn-bordred" onClick={() => navigate('/configuration-sfs?tab=4')
                  } />
                  <ButtonLink
                    type="primary"
                    comcode="validate"
                    htmlType="submit"
                    className="ut-reset-btn"
                    iconPosition="end"
                    onClick={() => handleSubmit()}
                  />
                </Row>
              </Col>
            </Row>
          </div>
        </Form>
      </div>
    </DF.Provider>
  );
};
export default ParrateformulaDetails;
